import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X, 
  FolderKanban, 
  FileText, 
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Button } from './ui/button';

interface JiraProject {
  id: string;
  key: string;
  name: string;
  projectTypeKey?: string;
  lead?: string;
}

interface JiraImportModalProps {
  onClose: () => void;
}

const JiraImportModal: React.FC<JiraImportModalProps> = ({ onClose }) => {
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'projects' | 'all'>('projects');
  const queryClient = useQueryClient();

  // Fetch Jira projects
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['jira-projects'],
    queryFn: () => apiClient.customRequest('/api/integrations/jira/projects'),
    retry: 1
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (data: { project_ids?: string[]; import_all?: boolean }) => {
      return apiClient.customRequest('/api/integrations/jira/import', {
        method: 'POST',
        body: data
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      const message = data.total_issues !== undefined
        ? `Successfully imported ${data.total_issues} issues from ${data.total_projects} project(s)!`
        : 'Jira data imported successfully!';
      alert(message);
      onClose();
    },
    onError: (error: any) => {
      alert(`Failed to import Jira data: ${error.message}`);
    }
  });

  const projects = Array.isArray(projectsData) ? projectsData : [];

  const handleProjectToggle = (projectId: string) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjects(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProjects.size === projects.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(projects.map((project: JiraProject) => project.id)));
    }
  };

  const handleImport = () => {
    if (activeTab === 'all') {
      importMutation.mutate({ import_all: true });
    } else if (selectedProjects.size > 0) {
      importMutation.mutate({ project_ids: Array.from(selectedProjects) });
    } else {
      alert('Please select at least one project to import');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <FolderKanban className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Import Jira Data</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select projects to import issues from
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'projects'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FolderKanban className="h-4 w-4" />
              <span>By Project ({projects.length})</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              <span>Import All Projects</span>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'projects' ? (
            <div>
              {/* Select All */}
              <div className="mb-4 flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedProjects.size === projects.length && projects.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select All Projects
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedProjects.size} of {projects.length} selected
                </span>
              </div>

              {/* Projects List */}
              {projectsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading projects...</p>
                  </div>
                </div>
              ) : projects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                    No Projects Found
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No projects available in your Jira workspace
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {projects.map((project: JiraProject) => (
                    <label
                      key={project.id}
                      className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProjects.has(project.id)}
                        onChange={() => handleProjectToggle(project.id)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{project.name}</h4>
                          <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full font-mono">
                            {project.key}
                          </span>
                          {project.projectTypeKey && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                              {project.projectTypeKey}
                            </span>
                          )}
                        </div>
                        {project.lead && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Lead: {project.lead}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="py-8">
              <div className="max-w-2xl mx-auto text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Import All Jira Projects
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  This will import all issues from all accessible projects updated in the last 90 days.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <FolderKanban className="h-6 w-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">All Projects</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{projects.length} available</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <FileText className="h-6 w-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Issues</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Last 90 days</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Date Range</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Updated since {new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Auto Sync</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Daily updates</p>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    💡 <strong>Tip:</strong> After the initial import, only new and updated issues will be synced daily to keep your data fresh.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'projects' ? (
              <span>
                {selectedProjects.size > 0 
                  ? `${selectedProjects.size} project${selectedProjects.size > 1 ? 's' : ''} selected`
                  : 'Select projects to import their issues'}
              </span>
            ) : (
              <span>Complete historical sync (last 90 days)</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={importMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || (activeTab === 'projects' && selectedProjects.size === 0)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {activeTab === 'all' ? 'Import All Projects' : `Import ${selectedProjects.size} Project${selectedProjects.size !== 1 ? 's' : ''}`}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JiraImportModal;
